/* import __COLOCATED_TEMPLATE__ from './api-connection.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
/* eslint-disable @intercom/intercom/no-bare-strings */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ApiConfiguration from 'embercom/models/standalone/api-configuration';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { type PulseAccordion } from 'glint/pulse';
import type Store from '@ember-data/store';
import type Token from 'embercom/models/custom-authentication/token';

const SUPPORTED_TOKEN_TYPES = ['text', 'http_request'] as const;

interface Args {
  accordion: PulseAccordion;
  apiConfig: ApiConfiguration;
}

export default class StandaloneApiConnection extends Component<Args> {
  @service declare store: Store;

  @tracked customAuthenticationTokens: Token[] | undefined;
  @tracked selectedTokenId = this.args.apiConfig.tokenId || undefined;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    taskFor(this.loadCustomAuthenticationTokens).perform();
  }

  @action async save() {
    this.args.apiConfig.save();
  }

  @action
  updateSelectedToken(tokenId: string) {
    this.args.apiConfig.tokenId = tokenId;
    this.selectedTokenId = tokenId;
  }

  @action
  clearSelectedToken() {
    this.args.apiConfig.tokenId = null;
    this.selectedTokenId = undefined;
  }

  @task({ drop: true })
  *loadCustomAuthenticationTokens(): TaskGenerator<void> {
    this.customAuthenticationTokens = yield this.store.findAll('custom-authentication/token');
  }

  get tokenList() {
    return [{ items: this.tokenGroupListItems }] as { items: { value: string; text: string }[] }[];
  }

  get tokenGroupListItems() {
    return this.customAuthenticationTokens
      ?.filter((token) =>
        SUPPORTED_TOKEN_TYPES.includes(token.tokenType as (typeof SUPPORTED_TOKEN_TYPES)[number]),
      )
      .map((token) => {
        return {
          value: token.id,
          text: token.name,
        };
      });
  }

  get selectedToken() {
    return this.customAuthenticationTokens?.find((token) => token.id === this.selectedTokenId);
  }

  get areTokensLoaded() {
    return this.customAuthenticationTokens !== undefined;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Api::ApiConnection': typeof StandaloneApiConnection;
    'standalone/api/api-connection': typeof StandaloneApiConnection;
  }
}
